import { useState, useEffect, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  fetchOrderItems,
  updateOrder,
  updateOrderItem,
  deleteOrderItem,
  deleteOrder,
} from "@/lib/api";
import AddProductDialog from "@/components/AddProductDialog";
import {
  Loader2,
  Package,
  ShieldCheck,
  Save,
  Trash2,
  Pencil,
  Plus,
  Truck,
  RefreshCw,
  PackageOpen,
} from "lucide-react";
import SplitOrderDialog from "@/components/SplitOrderDialog";
import { toast } from "sonner";

function fmt(n) {
  const x = Number(n || 0);
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const STATUS_OPTIONS = ["pending", "processing", "completed", "cancelled", "refunded"];

/**
 * Order detail side sheet — opens when a row is clicked. Loads line items with
 * MSRP / role price / paid total for each product. When the caller is admin,
 * an inline editor lets them change status / customer name / shipping / total.
 */
export default function OrderDetailSheet({
  orderRecordId,
  userRecordId,
  open,
  onOpenChange,
  isAdmin = false,
  initialEdit = false,
  onUpdated,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Admin edit state
  const [editing, setEditing] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftTotal, setDraftTotal] = useState("");
  const [draftShip, setDraftShip] = useState("");
  const [draftTax, setDraftTax] = useState("");
  const [draftDiscount, setDraftDiscount] = useState("");
  const [draftDiscountReason, setDraftDiscountReason] = useState("");
  const [draftAddress, setDraftAddress] = useState("");
  const [draftAddress2, setDraftAddress2] = useState("");
  const [draftCity, setDraftCity] = useState("");
  const [draftState, setDraftState] = useState("");
  const [draftZip, setDraftZip] = useState("");
  const [saving, setSaving] = useState(false);
  const [itemEditId, setItemEditId] = useState(null);
  const [addProductOpen, setAddProductOpen] = useState(false);

  useEffect(() => {
    if (!open || !orderRecordId) return;
    const ac = new AbortController();
    const run = async () => {
      setLoading(true);
      setErr(null);
      setEditing(isAdmin && initialEdit);
      try {
        const d = await fetchOrderItems(orderRecordId, userRecordId);
        if (ac.signal.aborted) return;
        setData(d);
        setDraftStatus((d?.order?.status || "").toString().toLowerCase());
        setDraftName(d?.order?.name || "");
        // Default the editable Total to the live re-computed value
        // (items + shipping + tax − discount) rather than the (often stale)
        // stored Total.
        const liveTotal =
          (d?.items || []).reduce(
            (s, it) => s + (Number(it.line_total) || 0),
            0
          ) +
          Number(d?.order?.shipping_charge || 0) +
          Number(d?.order?.tax || 0) -
          Number(d?.order?.discount_amount || 0);
        setDraftTotal(String(Math.round(Math.max(0, liveTotal) * 100) / 100));
        setDraftShip(d?.order?.shipping_charge ?? "");
        setDraftTax(d?.order?.tax ?? "");
        setDraftDiscount(d?.order?.discount_amount ?? "");
        setDraftDiscountReason(d?.order?.discount_reason ?? "");
        setDraftAddress(d?.order?.shipping_address ?? "");
        setDraftAddress2(d?.order?.shipping_address2 ?? "");
        setDraftCity(d?.order?.shipping_city ?? "");
        setDraftState(d?.order?.shipping_state ?? "");
        setDraftZip(d?.order?.shipping_zip ?? "");
      } catch (e) {
        if (ac.signal.aborted) return;
        setErr(e?.response?.data?.detail || e.message || "Could not load");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    run();
    return () => ac.abort();
  }, [open, orderRecordId, userRecordId, isAdmin, initialEdit]);

  const order = data?.order;

  // Live recomputed total = sum of current line item totals + shipping + tax.
  // The stored Total in Airtable can be stale (esp. after WooCommerce sync),
  // so we always show this beneath the editable Total field and auto-sync
  // draftTotal whenever items/shipping/tax change during editing.
  const computedTotal = useMemo(() => {
    if (!data) return 0;
    const sub = (data.items || []).reduce(
      (s, it) => s + (Number(it.line_total) || 0),
      0
    );
    const ship = Number(draftShip || 0) || 0;
    const tax = Number(draftTax || 0) || 0;
    const disc = Number(draftDiscount || 0) || 0;
    return Math.round(Math.max(0, sub + ship + tax - disc) * 100) / 100;
  }, [data, draftShip, draftTax, draftDiscount]);

  // While editing, keep draftTotal synced to computedTotal so the admin never
  // has to manually re-type it after editing line items / shipping / tax.
  // (Synced via a ref-driven side effect inside the line-item handlers below
  // and the explicit Recalc button; we deliberately don't useEffect to set
  // state from computedTotal so eslint's react-hooks/set-state-in-effect is
  // satisfied. Initial sync happens when the admin opens the editor.)
  async function save() {
    setSaving(true);
    const updates = {};
    if ((draftStatus || "") !== (order?.status || "").toLowerCase()) {
      updates.status = draftStatus;
    }
    if ((draftName || "") !== (order?.name || "")) updates.name = draftName;
    // The Total field is kept live-synced to (items + shipping + tax −
    // discount) by every onChange below, so just save what the admin sees.
    const effectiveTotal = Number(draftTotal);
    if (effectiveTotal !== Number(order?.total)) {
      updates.total = effectiveTotal;
    }
    if (Number(draftShip) !== Number(order?.shipping_charge)) {
      updates.shipping_charge = Number(draftShip);
    }
    // Tax: an empty field means the admin cleared/removed tax → persist 0
    // (the old `draftTax !== ""` guard silently skipped this, leaving stale tax
    // on the order while the total recomputed without it).
    const taxVal = draftTax === "" ? 0 : Number(draftTax) || 0;
    if (taxVal !== Number(order?.tax || 0)) {
      updates.tax = taxVal;
    }
    if (Number(draftDiscount || 0) !== Number(order?.discount_amount || 0)) {
      updates.discount_amount = Number(draftDiscount || 0);
    }
    if ((draftDiscountReason || "") !== (order?.discount_reason || "")) {
      updates.discount_reason = draftDiscountReason;
    }
    if ((draftAddress || "") !== (order?.shipping_address || "")) {
      updates.shipping_address = draftAddress;
    }
    if ((draftAddress2 || "") !== (order?.shipping_address2 || "")) {
      updates.shipping_address2 = draftAddress2;
    }
    if ((draftCity || "") !== (order?.shipping_city || "")) {
      updates.city = draftCity;
    }
    if ((draftState || "") !== (order?.shipping_state || "")) {
      updates.state = draftState;
    }
    if ((draftZip || "") !== (order?.shipping_zip || "")) {
      updates.zipcode = draftZip;
    }
    if (Object.keys(updates).length === 0) {
      toast.info("Nothing to update");
      setEditing(false);
      setSaving(false);
      return;
    }
    try {
      const res = await updateOrder(orderRecordId, userRecordId, updates);
      setData((prev) => ({ ...prev, order: res.order }));
      if (res.warning) {
        toast.warning(res.warning, { duration: 8000 });
      } else {
        toast.success("Order updated");
      }
      setEditing(false);
      onUpdated?.(res.order);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto p-0"
        data-testid="order-detail-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-zinc-200">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-lg font-semibold">
              {order ? `Order #${order.order_id}` : "Order details"}
            </SheetTitle>
            {isAdmin && order && editing && (
              <div className="flex items-center gap-2 mr-7">
                <Button size="sm" variant="outline" onClick={() => setSplitOpen(true)} className="h-8 rounded-lg text-xs border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="order-split-btn" title="Move back-ordered items to a separate B- order">
                  <PackageOpen size={12} className="mr-1" /> Split
                </Button>
              </div>
            )}
            {isAdmin && order && !editing && (
              <div className="flex items-center gap-2 mr-7">
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete Order #${order.order_id}? This cannot be undone.`)) return;
                    try {
                      await deleteOrder(orderRecordId, userRecordId);
                      toast.success("Order deleted");
                      onUpdated?.();
                      onOpenChange?.(false);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not delete order");
                    }
                  }}
                  className="h-8 w-8 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                  data-testid="order-delete-btn"
                  title="Delete order"
                >
                  <Trash2 size={14} />
                </button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSplitOpen(true)}
                  className="h-8 rounded-lg text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="order-split-btn-view"
                  title="Move back-ordered items to a separate B- order"
                >
                  <PackageOpen size={12} className="mr-1" />
                  Split
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="h-8 rounded-lg text-xs border-violet-200 text-violet-700 hover:bg-violet-50"
                  data-testid="order-edit-btn"
                >
                  <ShieldCheck size={12} className="mr-1" />
                  Edit (admin)
                </Button>
              </div>
            )}
          </div>
        </SheetHeader>
        {isAdmin && order && (
          <SplitOrderDialog open={splitOpen} onOpenChange={setSplitOpen} order={{ ...order, record_id: orderRecordId }} items={data?.items || []}
            callerRecId={userRecordId} onDone={() => { onUpdated?.(); onOpenChange?.(false); }} />
        )}

        {loading && (
          <div className="py-14 flex flex-col items-center text-zinc-500">
            <Loader2 className="animate-spin mb-2" />
            <span className="text-sm">Loading order…</span>
          </div>
        )}

        {err && (
          <div className="p-5 text-sm text-rose-700 bg-rose-50 m-4 rounded-xl border border-rose-200">
            {err}
          </div>
        )}

        {data && order && (
          <div className="p-5 space-y-4">
            {/* Admin edit panel */}
            {editing && (
              <div
                className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 space-y-3"
                data-testid="order-edit-panel"
              >
                <div className="text-[10px] uppercase tracking-widest font-semibold text-violet-700 flex items-center gap-1">
                  <ShieldCheck size={11} /> Admin edit
                </div>
                <EditField label="Status">
                  <select
                    value={draftStatus}
                    onChange={(e) => setDraftStatus(e.target.value)}
                    className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                    data-testid="order-edit-status"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </EditField>
                <EditField label="Customer name">
                  <input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                    data-testid="order-edit-name"
                  />
                </EditField>
                <div className="grid grid-cols-3 gap-2">
                  <EditField label="Total">
                    <div className="flex items-stretch gap-1">
                      <input
                        type="number"
                        step="0.01"
                        value={draftTotal}
                        onChange={(e) => setDraftTotal(e.target.value)}
                        className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                        data-testid="order-edit-total"
                      />
                      <button
                        type="button"
                        onClick={() => setDraftTotal(String(computedTotal))}
                        className="h-9 px-2 rounded-lg border border-violet-200 bg-white text-violet-700 hover:bg-violet-50 shrink-0"
                        title={`Recalculate: items + shipping + tax = ${fmt(computedTotal)}`}
                        data-testid="order-edit-total-recalc"
                      >
                        <RefreshCw size={12} />
                      </button>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1" data-testid="order-edit-total-hint">
                      auto = {fmt(computedTotal)}
                    </div>
                  </EditField>
                  <EditField label="Shipping">
                    <input
                      type="number"
                      step="0.01"
                      value={draftShip}
                      onChange={(e) => {
                        setDraftShip(e.target.value);
                        // keep Total in sync while editing
                        const sub = (data?.items || []).reduce(
                          (s, it) => s + (Number(it.line_total) || 0),
                          0
                        );
                        const t =
                          sub + (Number(e.target.value) || 0) + (Number(draftTax) || 0) - (Number(draftDiscount) || 0);
                        setDraftTotal(String(Math.round(Math.max(0, t) * 100) / 100));
                      }}
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                      data-testid="order-edit-shipping"
                    />
                  </EditField>
                  <EditField label="Tax">
                    <input
                      type="number"
                      step="0.01"
                      value={draftTax}
                      onChange={(e) => {
                        setDraftTax(e.target.value);
                        const sub = (data?.items || []).reduce(
                          (s, it) => s + (Number(it.line_total) || 0),
                          0
                        );
                        const t =
                          sub + (Number(draftShip) || 0) + (Number(e.target.value) || 0) - (Number(draftDiscount) || 0);
                        setDraftTotal(String(Math.round(Math.max(0, t) * 100) / 100));
                      }}
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                      data-testid="order-edit-tax"
                    />
                  </EditField>
                </div>
                {/* Discount */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-violet-100">
                  <EditField label="Discount $">
                    <input
                      type="number"
                      step="0.01"
                      value={draftDiscount}
                      onChange={(e) => {
                        setDraftDiscount(e.target.value);
                        const sub = (data?.items || []).reduce(
                          (s, it) => s + (Number(it.line_total) || 0),
                          0
                        );
                        const t =
                          sub +
                          (Number(draftShip) || 0) +
                          (Number(draftTax) || 0) -
                          (Number(e.target.value) || 0);
                        setDraftTotal(String(Math.round(Math.max(0, t) * 100) / 100));
                      }}
                      placeholder="0.00"
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                      data-testid="order-edit-discount"
                    />
                  </EditField>
                  <EditField label="Reason">
                    <input
                      value={draftDiscountReason}
                      onChange={(e) => setDraftDiscountReason(e.target.value)}
                      placeholder="e.g. Zelle 5%"
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                      data-testid="order-edit-discount-reason"
                    />
                  </EditField>
                </div>
                {/* Shipping address */}
                <div className="space-y-2 pt-2 border-t border-violet-100">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-violet-700">
                    Shipping address
                  </div>
                  <EditField label="Street address">
                    <input
                      value={draftAddress}
                      onChange={(e) => setDraftAddress(e.target.value)}
                      placeholder="123 Main St"
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                      data-testid="order-edit-address"
                    />
                  </EditField>
                  <EditField label="Apt / Suite (optional)">
                    <input
                      value={draftAddress2}
                      onChange={(e) => setDraftAddress2(e.target.value)}
                      placeholder="Apt 4B"
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                      data-testid="order-edit-address2"
                    />
                  </EditField>
                  <div className="grid grid-cols-3 gap-2">
                    <EditField label="City">
                      <input
                        value={draftCity}
                        onChange={(e) => setDraftCity(e.target.value)}
                        className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                        data-testid="order-edit-city"
                      />
                    </EditField>
                    <EditField label="State">
                      <input
                        value={draftState}
                        onChange={(e) => setDraftState(e.target.value)}
                        className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                        data-testid="order-edit-state"
                      />
                    </EditField>
                    <EditField label="Zip">
                      <input
                        value={draftZip}
                        onChange={(e) => setDraftZip(e.target.value)}
                        className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm bg-white"
                        data-testid="order-edit-zip"
                      />
                    </EditField>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving}
                    className="h-9 rounded-lg bg-violet-600 hover:bg-violet-700 text-white flex-1"
                    data-testid="order-edit-save"
                  >
                    {saving ? (
                      <Loader2 size={13} className="animate-spin mr-1" />
                    ) : (
                      <Save size={13} className="mr-1" />
                    )}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(false)}
                    className="h-9 rounded-lg"
                    data-testid="order-edit-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-zinc-500">Date</div>
              <div className="text-zinc-900 text-right">{order.date || "—"}</div>
              <div className="text-zinc-500">Status</div>
              <div className="text-zinc-900 text-right">
                <StatusBadge status={order.status} />
              </div>
              <div className="text-zinc-500">Customer</div>
              <div className="text-zinc-900 text-right truncate">
                {order.name ||
                  [order.customer_first_name, order.customer_last_name]
                    .filter(Boolean)
                    .join(" ")}
              </div>
              <div className="text-zinc-500">Email</div>
              <div className="text-zinc-900 text-right truncate text-xs">
                {order.customer_email || "—"}
              </div>
              <div className="text-zinc-500">Coupon</div>
              <div className="text-zinc-900 text-right">
                {order.coupon_code ? (
                  <span className="font-mono bg-violet-50 text-violet-700 text-xs px-2 py-0.5 rounded">
                    {order.coupon_code}
                  </span>
                ) : (
                  "—"
                )}
              </div>
              <div className="text-zinc-500">Tracking</div>
              <div className="text-zinc-900 text-right truncate">
                {order.tracking_number ? (
                  <a
                    href={order.tracking_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                    data-testid="order-detail-tracking"
                  >
                    <Truck size={12} className="shrink-0" />
                    {order.tracking_number}
                  </a>
                ) : (
                  "—"
                )}
              </div>
            </div>
            {order.acp && (
              <div className="mt-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-900 space-y-1" data-testid="order-detail-acp">
                <div className="flex items-center gap-2 font-semibold">
                  <span className="text-[10px] uppercase tracking-wider bg-sky-600 text-white px-1.5 py-0.5 rounded">ChatGPT</span>
                  Source: ChatGPT Instant Checkout{order.acp.simulated ? " · test" : ""}
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono text-[11px]">
                  <span className="text-sky-700">ACP Checkout Session</span><span className="truncate">{order.acp.checkout_session_id}</span>
                  <span className="text-sky-700">ACP Order ID</span><span className="truncate">{order.acp.acp_order_id}</span>
                  <span className="text-sky-700">Stripe Payment Intent</span><span className="truncate">{order.acp.payment_intent}</span>
                  <span className="text-sky-700">Promo Code</span><span>{order.acp.promo_code || "—"}</span>
                  <span className="text-sky-700">Discount</span><span>${Number(order.acp.discount).toFixed(2)}</span>
                  <span className="text-sky-700">Shipping / Tax</span><span>${Number(order.acp.shipping).toFixed(2)} / ${Number(order.acp.tax).toFixed(2)}</span>
                  <span className="text-sky-700">Total</span><span>${Number(order.acp.total).toFixed(2)}</span>
                </div>
              </div>
            )}
            {order.notes ? (
              <div
                className="mt-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-900"
                data-testid="order-detail-notes"
              >
                <span className="font-semibold">Note: </span>
                {order.notes}
              </div>
            ) : null}

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  Line items
                </div>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAddProductOpen(true)}
                    className="h-7 rounded-lg text-xs border-violet-200 text-violet-700 hover:bg-violet-50"
                    data-testid="order-add-product-btn"
                  >
                    <Plus size={12} className="mr-1" />
                    Add product
                  </Button>
                )}
              </div>
              {data.items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500">
                  <Package size={18} className="mx-auto text-zinc-400 mb-1" />
                  No line items found
                </div>
              ) : (
                <ul className="space-y-2" data-testid="order-items-list">
                  {data.items.map((it) => (
                    <LineItem
                      key={it.id}
                      item={it}
                      isAdmin={isAdmin}
                      isEditing={itemEditId === it.id}
                      onEdit={() => setItemEditId(it.id)}
                      onCancel={() => setItemEditId(null)}
                      onSaved={(updated) => {
                        setItemEditId(null);
                        setData((prev) => {
                          const items = prev.items.map((x) =>
                            x.id === updated.id
                              ? {
                                  ...x,
                                  name: updated.name || x.name,
                                  qty: updated.qty,
                                  role_price: updated.price,
                                  line_total: updated.total,
                                }
                              : x
                          );
                          const subtotal = items.reduce(
                            (s, x) => s + (Number(x.line_total) || 0),
                            0
                          );
                          // refresh draft total in edit mode
                          const t =
                            subtotal +
                            (Number(draftShip) || 0) +
                            (Number(draftTax) || 0);
                          setDraftTotal(String(Math.round(t * 100) / 100));
                          return { ...prev, items, subtotal };
                        });
                        toast.success("Line item updated");
                      }}
                      onDeleted={(id) => {
                        setData((prev) => {
                          const items = prev.items.filter((x) => x.id !== id);
                          const subtotal = items.reduce(
                            (s, x) => s + (Number(x.line_total) || 0),
                            0
                          );
                          const t =
                            subtotal +
                            (Number(draftShip) || 0) +
                            (Number(draftTax) || 0);
                          setDraftTotal(String(Math.round(t * 100) / 100));
                          return { ...prev, items, subtotal };
                        });
                        toast.success("Line item removed");
                      }}
                      orderRecordId={orderRecordId}
                      userRecordId={userRecordId}
                    />
                  ))}
                </ul>
              )}
            </div>

            {/* Totals */}
            <div className="rounded-xl bg-zinc-50 p-4 space-y-1.5 text-sm">
              <Row label="Subtotal" value={fmt(data.subtotal)} />
              <Row
                label="Shipping"
                value={fmt(editing ? Number(draftShip || 0) : order.shipping_charge)}
              />
              {(editing ? Number(draftTax || 0) > 0 : Number(order.tax || 0) > 0) && (
                <Row
                  label="Tax"
                  value={fmt(editing ? Number(draftTax || 0) : order.tax)}
                />
              )}
              {(editing ? Number(draftDiscount || 0) > 0 : Number(order.discount_amount || 0) > 0) && (
                <Row
                  label="Discount"
                  value={`-${fmt(editing ? Number(draftDiscount || 0) : order.discount_amount)}`}
                  testId="order-discount-row"
                />
              )}
              <div className="border-t border-zinc-200 my-2" />
              <Row
                label="Order total"
                value={fmt(editing ? computedTotal : order.total)}
                strong
                testId="order-total"
              />
            </div>
          </div>
        )}

        <AddProductDialog
          open={addProductOpen}
          onOpenChange={setAddProductOpen}
          orderRecordId={orderRecordId}
          userRecordId={userRecordId}
          onAdded={(newItem) => {
            // Normalize new item shape to match existing list items
            const norm = {
              id: newItem.id,
              name: newItem.name,
              qty: newItem.qty,
              role_price: newItem.price,
              msrp: newItem.price,
              line_total: newItem.total,
              image_url: newItem.image_url || "",
            };
            setData((prev) => {
              const items = [...(prev.items || []), norm];
              const subtotal = items.reduce(
                (s, x) => s + (Number(x.line_total) || 0),
                0
              );
              const t =
                subtotal + (Number(draftShip) || 0) + (Number(draftTax) || 0);
              setDraftTotal(String(Math.round(t * 100) / 100));
              return { ...prev, items, subtotal };
            });
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

function LineItem({
  item: it,
  isAdmin,
  isEditing,
  onEdit,
  onCancel,
  onSaved,
  onDeleted,
  userRecordId,
}) {
  const [qty, setQty] = useState(it.qty);
  const [price, setPrice] = useState(it.role_price);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const res = await updateOrderItem(it.id, userRecordId, {
        item_count: Number(qty) || 0,
        item_price: Number(price) || 0,
      });
      onSaved(res.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove ${it.name}?`)) return;
    setDeleting(true);
    try {
      await deleteOrderItem(it.id, userRecordId);
      onDeleted(it.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <li
      className="rounded-xl border border-zinc-200 p-3 flex gap-3"
      data-testid={`order-item-${it.id}`}
    >
      {it.image_url ? (
        <img
          src={it.image_url}
          alt={it.name}
          className="w-14 h-14 rounded-lg object-cover bg-zinc-100"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400">
          <Package size={20} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1">
          <div className="font-medium text-zinc-900 truncate">{it.name}</div>
          {isAdmin && !isEditing && (
            <div className="flex gap-1 shrink-0">
              <button
                onClick={onEdit}
                className="p-1 rounded hover:bg-violet-50 text-violet-700"
                aria-label="Edit line item"
                data-testid={`order-item-edit-${it.id}`}
              >
                <Pencil size={12} />
              </button>
              <button
                onClick={remove}
                disabled={deleting}
                className="p-1 rounded hover:bg-rose-50 text-rose-600"
                aria-label="Delete line item"
                data-testid={`order-item-delete-${it.id}`}
              >
                {deleting ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Trash2 size={12} />
                )}
              </button>
            </div>
          )}
        </div>
        {isEditing ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <EditField label="Qty">
              <input
                type="number"
                step="1"
                min="0"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="h-8 w-full rounded-md border border-zinc-200 px-2 text-sm bg-white"
                data-testid={`order-item-qty-${it.id}`}
              />
            </EditField>
            <EditField label="Price">
              <input
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="h-8 w-full rounded-md border border-zinc-200 px-2 text-sm bg-white"
                data-testid={`order-item-price-${it.id}`}
              />
            </EditField>
            <div className="col-span-2 flex gap-2">
              <Button
                size="sm"
                onClick={save}
                disabled={saving}
                className="h-8 rounded-lg bg-violet-600 hover:bg-violet-700 text-white flex-1"
                data-testid={`order-item-save-${it.id}`}
              >
                {saving ? (
                  <Loader2 size={11} className="animate-spin mr-1" />
                ) : (
                  <Save size={11} className="mr-1" />
                )}
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancel}
                className="h-8 rounded-lg"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs text-zinc-500 mt-0.5">Qty: {it.qty}</div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
              <PriceCell label="MSRP" value={fmt(it.msrp)} muted />
              <PriceCell label="Role price" value={fmt(it.role_price)} />
              <PriceCell label="Paid total" value={fmt(it.line_total)} strong />
            </div>
          </>
        )}
      </div>
    </li>
  );
}

function PriceCell({ label, value, muted, strong }) {
  return (
    <div className="rounded-md bg-zinc-50 px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div
        className={`mt-0.5 ${strong ? "font-semibold text-zinc-900" : muted ? "text-zinc-500" : "text-zinc-800"}`}
      >
        {value}
      </div>
    </div>
  );
}

function Row({ label, value, strong, testId }) {
  return (
    <div className="flex justify-between" data-testid={testId}>
      <span className="text-zinc-500">{label}</span>
      <span className={strong ? "font-semibold text-zinc-900" : "text-zinc-800"}>
        {value}
      </span>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toString().toLowerCase();
  const tone = {
    completed: "bg-emerald-50 text-emerald-700",
    processing: "bg-blue-50 text-blue-700",
    pending: "bg-amber-50 text-amber-700",
    cancelled: "bg-rose-50 text-rose-700",
    refunded: "bg-zinc-100 text-zinc-700",
  }[s] || "bg-zinc-100 text-zinc-700";
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${tone}`}
    >
      {status || "—"}
    </span>
  );
}
